import React, { useRef, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from '../config/api';

export default function Profile() {
    const states = [
        "AK", "AL", "AR", "AZ", "CA", "CO", "CT", "DE", "DC", "FL",
        "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
        "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH",
        "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI",
        "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
        "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK"
    ];

    const serviceOptions = [
        { value: 'commercial', label: 'Commercial' },
        { value: 'residential', label: 'Residential' },
        { value: 'federal', label: 'Federal' },
        { value: 'road_construction_and_industrial', label: 'Road & Industrial' }
    ];

    const scopeOptions = [
        { value: 'site_preparation', label: 'Site Preparation' },
        { value: 'concrete', label: 'Concrete' },
        { value: 'structural_and_framing', label: 'Structural & Framing' },
        { value: 'roofing_siding_and_sheet_metal_work', label: 'Roofing & Sheet Metal' },
        { value: 'plumbing', label: 'Plumbing' },
        { value: 'hvac', label: 'HVAC' },
        { value: 'electrical', label: 'Electrical' },
        { value: 'carpentry', label: 'Carpentry' },
        { value: 'drywall', label: 'Drywall' },
        { value: 'painting_and_paper_hanging', label: 'Painting & Wallpaper' }
    ];

    const userData = useSelector((state) => state?.userProfileSlice?.userData?.data);
    const userRole = useSelector((state) => state?.userProfileSlice?.userData?.data?.role);

    const [profile, setProfile] = useState(userData?.profile_image || null);
    const [w9, setW9] = useState(userData?.w9_form || null);
    const [workerComp, setWorkerComp] = useState(userData?.worker_comp || null);
    const [isWorkerComp, setIsWorkerComp] = useState(false);
    const [isW9, setIsW9] = useState(false);
    const [isProfile, setIsProfile] = useState(false);

    const w9Document = useRef(null);
    const workersDocument = useRef(null);
    const profilePicture = useRef(null);

    const handleReset = () => { if (w9Document.current) w9Document.current.value = ""; };
    const handleResetWorkers = () => { if (workersDocument.current) workersDocument.current.value = ""; };
    const handleResetProfile = () => { if (profilePicture.current) profilePicture.current.value = ""; };

    const getFileNameFromURL = (url) => {
        if (!url) return 'Document';
        const urlParts = url.split('/');
        const filename = urlParts[urlParts.length - 1];
        const pattern = /(?:\d+_)+(.+\.pdf)$/;
        const match = filename.match(pattern);
        if (match && match.length > 1) {
            const extractedText = match[1].substring(10);
            return extractedText.length > 30 ? `${extractedText.slice(0, 30)}...` : extractedText;
        }
        return filename.length > 30 ? `${filename.slice(0, 30)}...` : filename;
    };

    const validationSchema = Yup.object().shape({
        projectName: Yup.string().required('Company name is required'),
        yearsInBusiness: Yup.number().required('Years in business is required').integer('Must be a whole number'),
        ein: Yup.string().required('EIN is required').matches(/^\d{2}-\d{5}$/, 'Format: XX-XXXXX'),
        licensedWorkStates: Yup.string().required('Licensed state is required'),
        contractorLicense: Yup.string().required('License number is required'),
        workCapacity: Yup.number().required('Work capacity is required'),
        numEmployees: Yup.number().required('Number of employees is required'),
        description: Yup.string().required('Description is required'),
        pastContractorsWorkedWith: Yup.string().required('Past contractors is required'),
    });

    const initialValues = {
        projectName: userData?.company_name || '',
        yearsInBusiness: userData?.business_year || '',
        ein: userData?.ein || '',
        licensedWorkStates: userData?.licensed_states_of_work || '',
        contractorLicense: userData?.contractor_license || '',
        workCapacity: userData?.work_capacity || '',
        numEmployees: userData?.number_of_employees || '',
        description: userData?.biography || '',
        selectedServices: userData?.services?.map(s => s.service) || [],
        scope: userData?.scope?.map(s => s.scope) || [],
        pastContractorsWorkedWith: userData?.past_contractors || '',
    };

    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const token = localStorage.getItem('authToken');
            const url = API_BASE_URL;

            if (token) {
                const formData = new FormData();
                formData.append('role', userRole);
                formData.append('company_name', values.projectName);
                formData.append('business_year', values.yearsInBusiness);
                formData.append('ein', values.ein);
                formData.append('licensed_states_of_work', values.licensedWorkStates);
                formData.append('contractor_license', values.contractorLicense);
                formData.append('work_capacity', values.workCapacity);
                formData.append('biography', values.description);
                formData.append('number_of_employees', values.numEmployees);
                if (isWorkerComp) formData.append('worker_comp', values.workersCompForm || workerComp);
                if (isW9) formData.append('w9_form', values.w9Form || w9);
                if (isProfile) formData.append('profile_image', values.profilePicture || profile);
                formData.append('commercial_services', 1);
                formData.append('residential_services', 1);
                formData.append('federal_services', 1);
                formData.append('road_and_industrial_construction', 1);
                values.scope.forEach((scope) => formData.append("identify_scope[]", scope));
                values.selectedServices.forEach((service) => formData.append("services[]", service));
                formData.append('past_contractors', values.pastContractorsWorkedWith);

                const response = await fetch(`${url}/update_contractor_profile`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                    body: formData,
                });

                const data = await response.json();
                if (data.success) {
                    toast.success('Profile updated successfully!', { autoClose: 3000 });
                } else {
                    toast.error(data.message, { autoClose: 3000 });
                }
            }
        } catch (error) {
            toast.error('An error occurred: ' + error, { autoClose: 3000 });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="profile_page">
            <div className="container">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, setFieldValue }) => (
                        <Form>
                            {/* Company Information Section */}
                            <div className="profile_section">
                                <div className="section_header">
                                    <h3>Company Information</h3>
                                    <p>Basic details about your business</p>
                                </div>
                                <div className="section_content">
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <div className="form_group">
                                                <label htmlFor="projectName">Company Name</label>
                                                <Field type="text" id="projectName" name="projectName" className="form-control" placeholder="Enter your company name" />
                                                <ErrorMessage name="projectName" component="div" className="field_error" />
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="form_group">
                                                <label htmlFor="yearsInBusiness">Years In Business</label>
                                                <Field type="number" id="yearsInBusiness" name="yearsInBusiness" className="form-control" placeholder="e.g. 10" />
                                                <ErrorMessage name="yearsInBusiness" component="div" className="field_error" />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="form_group">
                                                <label htmlFor="description">Company Description</label>
                                                <Field as="textarea" id="description" name="description" className="form-control" rows="4" placeholder="Describe your company and the services you provide..." />
                                                <ErrorMessage name="description" component="div" className="field_error" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Licensing & Compliance Section */}
                            <div className="profile_section">
                                <div className="section_header">
                                    <h3>Licensing & Compliance</h3>
                                    <p>Your business credentials and documentation</p>
                                </div>
                                <div className="section_content">
                                    <div className="row">
                                        <div className="col-lg-4 col-md-6">
                                            <div className="form_group">
                                                <label htmlFor="ein">EIN (Tax ID)</label>
                                                <Field type="text" id="ein" name="ein" className="form-control" placeholder="XX-XXXXX" />
                                                <ErrorMessage name="ein" component="div" className="field_error" />
                                            </div>
                                        </div>
                                        <div className="col-lg-4 col-md-6">
                                            <div className="form_group">
                                                <label htmlFor="licensedWorkStates">Licensed State</label>
                                                <Field name="licensedWorkStates" as="select" className="form-control">
                                                    <option value="" disabled>Select State</option>
                                                    {states.map((state, idx) => (
                                                        <option value={state} key={idx}>{state}</option>
                                                    ))}
                                                </Field>
                                                <ErrorMessage name="licensedWorkStates" component="div" className="field_error" />
                                            </div>
                                        </div>
                                        <div className="col-lg-4 col-md-6">
                                            <div className="form_group">
                                                <label htmlFor="contractorLicense">Contractor License #</label>
                                                <Field type="text" id="contractorLicense" name="contractorLicense" className="form-control" placeholder="License number" />
                                                <ErrorMessage name="contractorLicense" component="div" className="field_error" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="documents_grid">
                                        <div className="document_upload">
                                            <label>W9 Form</label>
                                            {w9 !== null ? (
                                                <div className="uploaded_file">
                                                    <div className="file_info">
                                                        <span className="file_icon">PDF</span>
                                                        <span className="file_name">{isW9 ? w9.name : getFileNameFromURL(userData?.w9_form)}</span>
                                                    </div>
                                                    <button type="button" className="remove_file" onClick={() => setW9(null)}>
                                                        <CloseIcon fontSize="small" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="file_dropzone">
                                                    <input type="file" name="w9Form" accept=".pdf" ref={w9Document} onChange={(e) => {
                                                        if (e.currentTarget.files[0]?.type === 'application/pdf') {
                                                            setFieldValue("w9Form", e.currentTarget.files[0]);
                                                            setW9(e.currentTarget.files[0]);
                                                            setIsW9(true);
                                                        } else {
                                                            setFieldValue("w9Form", null);
                                                            handleReset();
                                                        }
                                                    }} />
                                                    <span>Upload PDF</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="document_upload">
                                            <label>Worker's Comp</label>
                                            {workerComp !== null ? (
                                                <div className="uploaded_file">
                                                    <div className="file_info">
                                                        <span className="file_icon">PDF</span>
                                                        <span className="file_name">{isWorkerComp ? workerComp.name : getFileNameFromURL(userData?.worker_comp)}</span>
                                                    </div>
                                                    <button type="button" className="remove_file" onClick={() => setWorkerComp(null)}>
                                                        <CloseIcon fontSize="small" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="file_dropzone">
                                                    <input type="file" accept=".pdf" ref={workersDocument} name="workersCompForm" onChange={(e) => {
                                                        if (e.currentTarget.files[0]?.type === 'application/pdf') {
                                                            setFieldValue("workersCompForm", e.currentTarget.files[0]);
                                                            setWorkerComp(e.currentTarget.files[0]);
                                                            setIsWorkerComp(true);
                                                        } else {
                                                            setFieldValue("workersCompForm", null);
                                                            handleResetWorkers();
                                                        }
                                                    }} />
                                                    <span>Upload PDF</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="document_upload">
                                            <label>Profile Photo</label>
                                            {profile !== null ? (
                                                <div className="uploaded_file has_image">
                                                    <img src={profile instanceof File ? URL.createObjectURL(profile) : profile} alt="Profile" className="profile_thumb" />
                                                    <button type="button" className="remove_file" onClick={() => setProfile(null)}>
                                                        <CloseIcon fontSize="small" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="file_dropzone">
                                                    <input type="file" accept=".jpg,.jpeg,.png" ref={profilePicture} name="profilePicture" onChange={(e) => {
                                                        if (e.currentTarget.files[0] && /^image\/(jpeg|jpg|png)$/.test(e.currentTarget.files[0].type)) {
                                                            setFieldValue("profilePicture", e.currentTarget.files[0]);
                                                            setProfile(e.currentTarget.files[0]);
                                                            setIsProfile(true);
                                                        } else {
                                                            setFieldValue("profilePicture", null);
                                                            handleResetProfile();
                                                        }
                                                    }} />
                                                    <span>Upload Image</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Capacity & Team Section */}
                            <div className="profile_section">
                                <div className="section_header">
                                    <h3>Capacity & Team</h3>
                                    <p>Your workforce and project capacity</p>
                                </div>
                                <div className="section_content">
                                    <div className="row">
                                        <div className="col-lg-4 col-md-6">
                                            <div className="form_group">
                                                <label htmlFor="numEmployees">Number of Employees</label>
                                                <Field type="number" id="numEmployees" name="numEmployees" className="form-control" placeholder="e.g. 25" />
                                                <ErrorMessage name="numEmployees" component="div" className="field_error" />
                                            </div>
                                        </div>
                                        <div className="col-lg-4 col-md-6">
                                            <div className="form_group">
                                                <label htmlFor="workCapacity">Concurrent Jobs Capacity</label>
                                                <Field type="number" id="workCapacity" name="workCapacity" className="form-control" placeholder="e.g. 5" />
                                                <ErrorMessage name="workCapacity" component="div" className="field_error" />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="form_group">
                                                <label htmlFor="pastContractorsWorkedWith">Past Contractors Worked With</label>
                                                <Field as="textarea" id="pastContractorsWorkedWith" name="pastContractorsWorkedWith" className="form-control" rows="3" placeholder="List contractors you've worked with previously..." />
                                                <ErrorMessage name="pastContractorsWorkedWith" component="div" className="field_error" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Services & Scope Section */}
                            <div className="profile_section">
                                <div className="section_header">
                                    <h3>Services & Expertise</h3>
                                    <p>Select the services and scope of work you provide</p>
                                </div>
                                <div className="section_content">
                                    <div className="row">
                                        <div className="col-lg-6">
                                            <div className="checkbox_group">
                                                <h4>Service Types</h4>
                                                <div className="checkbox_grid">
                                                    {serviceOptions.map((service) => (
                                                        <label key={service.value} className="checkbox_item">
                                                            <Field type="checkbox" name="selectedServices" value={service.value} />
                                                            <span className="checkmark"></span>
                                                            <span className="checkbox_label">{service.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-6">
                                            <div className="checkbox_group">
                                                <h4>Work Scope</h4>
                                                <div className="checkbox_grid two_columns">
                                                    {scopeOptions.map((scope) => (
                                                        <label key={scope.value} className="checkbox_item">
                                                            <Field type="checkbox" name="scope" value={scope.value} />
                                                            <span className="checkmark"></span>
                                                            <span className="checkbox_label">{scope.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="profile_actions">
                                <button type="submit" disabled={isSubmitting} className="save_btn">
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Saving...
                                        </>
                                    ) : 'Save Changes'}
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>
        </div>
    );
}
